import React from "react";
import { Box, Card, CardContent, Chip, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import type { Product } from "../../services/apiService";

const PLACEHOLDER_IMAGE = "/images/products/placeholder.png";

/** Catalog card — the whole card links to the product detail page. */
const ProductCard: React.FC<{ product: Product }> = ({ product }) => (
  <Card
    component={RouterLink}
    to={`/products/${product.slug}`}
    aria-label={`View details for ${product.name}`}
    sx={{
      display: "flex",
      flexDirection: "column",
      height: "100%",
      textDecoration: "none",
      color: "inherit",
    }}
  >
    <Box
      sx={{
        position: "relative",
        bgcolor: "#FFFFFF",
        borderBottom: "1px solid",
        borderColor: "divider",
        p: 2.5,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: 210,
      }}
    >
      <Box
        component="img"
        src={product.image}
        alt={product.name}
        loading="lazy"
        onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
          e.currentTarget.src = PLACEHOLDER_IMAGE;
        }}
        sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
      />
      {product.isNew && (
        <Chip
          label="New"
          color="secondary"
          size="small"
          sx={{ position: "absolute", top: 12, right: 12 }}
        />
      )}
    </Box>
    <CardContent sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
      <Chip
        label={product.category}
        size="small"
        sx={{
          alignSelf: "flex-start",
          mb: 1.5,
          bgcolor: "rgba(199,93,44,0.1)",
          color: "secondary.dark",
        }}
      />
      <Typography variant="h6" component="h3" sx={{ fontWeight: 700, mb: 1 }}>
        {product.name}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          mb: 2,
          flexGrow: 1,
          display: "-webkit-box",
          WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {product.description}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 700,
          color: "secondary.dark",
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        View details <ArrowForwardIcon sx={{ fontSize: 16 }} />
      </Typography>
    </CardContent>
  </Card>
);

export default ProductCard;
